import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";

export interface ICourier {
  _id?: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface IStore {
  /** State */
  oneCourier: ICourier | null;
  allCouriers: ICourier[];
  addedCourier?: string | null;
  updatedCourier?: string | null;
  /** Callbacks */
  getAllCouriers: () => void;
  getOneCourier: (courierId: string) => void;
  addCourier: (data: ICourier) => void;
  deleteCourier: (courierId: string) => void;
  updateCourier: (courierId: string, data: { name: string }) => void;
  clearAddedCourier: () => void;
  clearUpdatedCourier: () => void;
}

export const useCouriers = create<IStore>()((set, get) => ({
  /** State */
  allCouriers: [],
  oneCourier: null,
  addedCourier: null,
  updatedCourier: null,
  /** Callbacks */
  getAllCouriers: async () => {
    const currentCouriers = get().allCouriers;
    if (currentCouriers?.length) return;
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`couriers`);
      if (response?.data) {
        set({ allCouriers: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching all Couriers", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneCourier: async (courierId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`couriers/${courierId}`);
      if (response?.data) {
        set({ oneCourier: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one Courier", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addCourier: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("couriers", body);
      if (response?.data) {
        const newCourier = response.data;
        set((state) => ({
          allCouriers: [newCourier, ...state.allCouriers],
          addedCourier: newCourier._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Courier", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteCourier: async (courierId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`couriers/${courierId}`);
      set((state) => ({
        allCouriers: state.allCouriers.filter(
          (courier) => courier._id !== courierId
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting a Courier", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateCourier: async (courierId, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`couriers/${courierId}`, body);
      if (response?.data) {
        const updatedCourier = response.data;
        set((state) => ({
          allCouriers: state.allCouriers.map((courier) => {
            if (courier._id === updatedCourier._id) {
              return updatedCourier;
            }
            return courier;
          }),
          updatedCourier: updatedCourier._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating a Courier", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedCourier: () => {
    set({ addedCourier: null });
  },
  clearUpdatedCourier: () => {
    set({ updatedCourier: null });
  },
}));
